import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, Loader2, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { MicButton } from '@/components/MicButton';
import { toast } from 'sonner';
import { intakeApi } from '@/lib/api';
import { INTAKE } from '@/constants/testIds';

const ISSUES = [
  'None / generally healthy',
  'Overweight',
  'Diabetes / blood sugar',
  'High blood pressure',
  'High cholesterol',
  'Chronic fatigue',
  'Joint pain / arthritis',
  'Digestive issues',
  'Anxiety / stress',
  'Poor sleep',
  'Skin issues',
  'Hormonal imbalance',
  'Autoimmune condition',
  'Heart condition',
];

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const Intake = () => {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [age, setAge] = useState('');
  const [height, setHeight] = useState('');
  const [weight, setWeight] = useState('');
  const [selectedIssues, setSelectedIssues] = useState([]);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const toggleIssue = (i) => setSelectedIssues((prev) => prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]);

  const steps = [
    { key: 'age', label: 'How old are you?', sub: 'Age helps me tune your nutrient and mobility guidance.' },
    { key: 'height', label: 'How tall are you?', sub: 'Feet & inches (e.g. 5 ft 10 in) or centimeters — whatever feels natural.' },
    { key: 'weight', label: 'What do you weigh?', sub: 'In pounds. This calibrates your supplement dosages.' },
    { key: 'issues', label: 'What are you working through?', sub: 'Past & current — pick anything that resonates, or none.' },
  ];

  const canNext = () => {
    if (step === 0) return Number(age) > 0 && Number(age) < 121;
    if (step === 1) return height.trim().length > 0;
    if (step === 2) return Number(weight) > 20;
    return true;
  };

  const submit = async () => {
    setLoading(true);
    try {
      const payload = {
        age: Number(age),
        height: height.trim(),
        weight_lbs: Number(weight),
        health_issues: selectedIssues,
        notes: notes.trim(),
      };
      const result = await intakeApi.analyze(payload);
      sessionStorage.setItem('wellth_latest_report', JSON.stringify(result));
      nav('/evaluation-ready');
    } catch (e) {
      const msg = e.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const progress = ((step + 1) / steps.length) * 100;

  return (
    <div className="max-w-2xl mx-auto px-6 py-12 sm:py-20 min-h-[80vh]">
      {/* Progress bar */}
      <div className="mb-10">
        <div className="flex items-center justify-between text-xs tracking-[0.2em] uppercase font-semibold text-[#4A5A53] mb-3">
          <span>Step {step + 1} of {steps.length}</span>
          <span>{Math.round(progress)}%</span>
        </div>
        <div className="h-1 rounded-full bg-[#E5E1D8] overflow-hidden">
          <motion.div
            initial={false}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            className="h-full bg-[#4A7C59]"
          />
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.35 }}
        >
          <h1 className="font-serif-display text-4xl sm:text-5xl leading-tight text-[#1A2421] font-medium mb-3">{steps[step].label}</h1>
          <p className="text-[#4A5A53] mb-10 font-light">{steps[step].sub}</p>

          {step === 0 && (
            <Input
              type="number"
              inputMode="numeric"
              placeholder="e.g. 34"
              value={age}
              onChange={(e) => setAge(e.target.value)}
              className="h-14 text-lg bg-white border-[#E5E1D8] focus-visible:ring-[#7E9979] rounded-xl"
              data-testid={INTAKE.ageInput}
            />
          )}
          {step === 1 && (
            <Input
              placeholder="e.g. 5 ft 10 in or 178 cm"
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              className="h-14 text-lg bg-white border-[#E5E1D8] focus-visible:ring-[#7E9979] rounded-xl"
              data-testid={INTAKE.heightInput}
            />
          )}
          {step === 2 && (
            <Input
              type="number"
              inputMode="decimal"
              placeholder="e.g. 175"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              className="h-14 text-lg bg-white border-[#E5E1D8] focus-visible:ring-[#7E9979] rounded-xl"
              data-testid={INTAKE.weightInput}
            />
          )}
          {step === 3 && (
            <div className="space-y-6">
              <div className="flex flex-wrap gap-2">
                {ISSUES.map((issue) => {
                  const active = selectedIssues.includes(issue);
                  return (
                    <button
                      key={issue}
                      onClick={() => toggleIssue(issue)}
                      className={`px-4 py-2.5 rounded-full border text-sm transition-all ${active ? 'bg-[#4A7C59] text-white border-[#4A7C59]' : 'bg-white text-[#1A2421] border-[#E5E1D8] hover:border-[#7E9979]'}`}
                      data-testid={INTAKE.issueChip(slug(issue))}
                      type="button"
                    >
                      {issue}
                    </button>
                  );
                })}
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs tracking-[0.2em] uppercase font-semibold text-[#4A5A53]">Anything else? (optional)</label>
                  <MicButton testId="intake-notes-mic" onText={(t) => setNotes((v) => (v ? `${v} ${t}` : t))} />
                </div>
                <Textarea
                  placeholder="Anything specific I should know — allergies, energy dips, goals?"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="mt-2 min-h-[100px] bg-white border-[#E5E1D8] focus-visible:ring-[#7E9979] rounded-xl"
                  data-testid={INTAKE.notesInput}
                />
              </div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="flex items-center justify-between mt-12">
        <Button
          variant="ghost"
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          disabled={step === 0 || loading}
          className="rounded-full text-[#4A5A53] hover:text-[#4A7C59] gap-2"
          data-testid={INTAKE.backButton}
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back
        </Button>
        {step < steps.length - 1 ? (
          <Button
            onClick={() => setStep((s) => s + 1)}
            disabled={!canNext()}
            className="rounded-full bg-[#4A7C59] hover:bg-[#3E6B4F] text-white px-8 py-6 h-auto gap-2"
            data-testid={INTAKE.nextButton}
          >
            Continue <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Button>
        ) : (
          <Button
            onClick={submit}
            disabled={loading}
            className="rounded-full bg-[#4A7C59] hover:bg-[#3E6B4F] text-white px-8 py-6 h-auto gap-2"
            data-testid={INTAKE.submitButton}
          >
            {loading ? (<><Loader2 className="w-4 h-4 animate-spin" /> Crafting your plan…</>) : (<><Heart className="w-4 h-4 fill-white" strokeWidth={1.5} /> Get my plan</>)}
          </Button>
        )}
      </div>
    </div>
  );
};

export default Intake;
